import { ColumnHeaderIcon } from '../../icons';
import { cn } from '../../lib/utils';
import type { SortDirection } from '../ColumnFilterPopover';

export type { SortDirection };

export interface SortableColumnHeaderProps {
  /** Visible column label. */
  label: string;
  /** Current sort direction (asc/desc/null). */
  sortDirection?: SortDirection;
  /** Fired when the sort caret is clicked. Omit to hide the caret. */
  onSortToggle?: () => void;
  /** Whether the column has an active filter. */
  isFilterActive?: boolean;
  /** Fired when the filter funnel is clicked. Omit to hide the funnel. */
  onFilterTrigger?: () => void;
  /**
   * When this header is rendered inside a native `<th>` element, the wrapping
   * `<th>` already exposes the implicit `columnheader` role per the HTML/ARIA
   * spec — the WAI-ARIA "Presentational Roles Conflict Resolution" rules make
   * `role="presentation"` on a required owned element a no-op, so the inner
   * `role="columnheader"` here would produce a double announcement on JAWS /
   * NVDA. Set this flag to `true` from grid consumers that wrap the molecule
   * in `<th aria-sort=…>`; the molecule then renders as a layout-only `<div>`
   * with no ARIA semantics. Default `false` keeps the standalone behavior
   * (the molecule owns the `columnheader` role + `aria-sort` itself).
   */
  inTableHeaderCell?: boolean;
  /** Optional className appended to the root container. */
  className?: string;
}

function ariaSortValue(direction: SortDirection | undefined): 'ascending' | 'descending' | 'none' {
  if (direction === 'asc') return 'ascending';
  if (direction === 'desc') return 'descending';
  return 'none';
}

/**
 * SortableColumnHeader — column-header chrome per Figma `289:45672` + design.md
 * § 3.2. Renders the label, a 10×10 sort caret (`ColumnHeaderIcon kind="sortCaret"`)
 * and a 10×10 filter funnel (`ColumnHeaderIcon kind="filterFunnel"`) — each
 * shown only when its handler is provided. The filter funnel reflects
 * `isFilterActive` via `aria-pressed`.
 */
export function SortableColumnHeader({
  label,
  sortDirection = null,
  onSortToggle,
  isFilterActive = false,
  onFilterTrigger,
  inTableHeaderCell = false,
  className,
}: SortableColumnHeaderProps) {
  const ariaProps = inTableHeaderCell
    ? ({} as const)
    : ({ role: 'columnheader', 'aria-sort': ariaSortValue(sortDirection) } as const);
  return (
    <div
      data-testid="column-header"
      {...ariaProps}
      className={cn('inline-flex items-center gap-1', className)}
    >
      <span className="whitespace-nowrap font-outfit text-[10px] font-normal leading-[1.4] text-grey-600">
        {label}
      </span>
      {onSortToggle || onFilterTrigger ? (
        <span className="inline-flex items-center">
          {onSortToggle ? (
            <button
              type="button"
              aria-label={`Sort by ${label}`}
              onClick={onSortToggle}
              className="inline-flex h-2.5 w-2.5 items-center justify-center rounded outline-none focus-visible:ring-1 focus-visible:ring-dawn-orange"
            >
              <ColumnHeaderIcon
                kind="sortCaret"
                className={cn(
                  'text-grey-400',
                  sortDirection === 'asc' && 'text-dawn-black',
                  sortDirection === 'desc' && 'text-dawn-black rotate-180'
                )}
              />
            </button>
          ) : null}
          {onFilterTrigger ? (
            <button
              type="button"
              aria-label={`Filter ${label}`}
              aria-pressed={isFilterActive}
              onClick={onFilterTrigger}
              className="inline-flex h-2.5 w-2.5 items-center justify-center rounded outline-none focus-visible:ring-1 focus-visible:ring-dawn-orange"
            >
              <ColumnHeaderIcon
                kind="filterFunnel"
                className={cn('text-grey-400', isFilterActive && 'text-dawn-orange')}
              />
            </button>
          ) : null}
        </span>
      ) : null}
    </div>
  );
}

SortableColumnHeader.displayName = 'SortableColumnHeader';
